import React from 'react';
import { Mail, Phone, MapPin, MessageSquare, CheckCircle, ArrowRight, ExternalLink } from 'lucide-react';
import { useForm } from '@formspree/react';
import SEOHead from '../components/SEOHead';

export default function Contact() {
  const [state, handleSubmit] = useForm("xovnpllp");

  const contactInfo = [
    {
      icon: Phone,
      title: "Feel Free To Call Us At",
      value: "844-219-3935",
      href: "tel:844-219-3935",
      description: "24/7 Support Available"
    },
    {
      icon: Mail,
      title: "Feel Free To Email Us At",
      value: "sales@ucx.co",
      href: "mailto:sales@ucx.co",
      description: "We'll respond promptly"
    },
    {
      icon: MapPin,
      title: "Feel Free To Visit Us At",
      value: "1348 Hwy 287, Suite 300",
      subValue: "Waxahachie, TX 75165",
      href: "https://www.google.ca/maps/place/Unified+ConneXions/@32.4232846,-96.8506022,17.34z/",
      description: "24/7 Support Available"
    }
  ];

  return (
    <>
      <SEOHead 
        title="Contact UCX - Texas Managed IT Services | Get Free IT Consultation | 844-219-3935"
        description="Contact UCX for managed IT services in Texas. Get a free consultation for your business IT needs. Call 844-219-3935 or visit our Waxahachie office. Serving Dallas, Fort Worth, Austin, Houston."
        keywords="contact UCX, Texas IT services contact, managed IT consultation, IT support contact Dallas, UCX phone number, IT services quote Texas, managed service provider contact"
        canonical="https://ucx.co/contact"
      />
      <div className="pt-16">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80')] opacity-10 bg-cover bg-center"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/50 to-blue-900/90"></div>
        <div className="absolute inset-0" style={{ 
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200">
                Let's Connect
              </span>
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Get in touch with our team of experts and discover how we can transform your IT infrastructure.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Cards */}
      <div className="relative -mt-16 mb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {contactInfo.map((info, index) => (
              <a
                key={index}
                href={info.href}
                target={info.icon === MapPin ? "_blank" : undefined}
                rel={info.icon === MapPin ? "noopener noreferrer" : undefined}
                className="group relative bg-white rounded-xl shadow-lg p-8 border border-gray-100 transform hover:-translate-y-1 transition-all duration-300"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl opacity-0 group-hover:opacity-5 transition-opacity"></div>
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg text-blue-600 group-hover:scale-110 transition-transform duration-300">
                    <info.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">{info.title}</h3>
                    <p className="text-lg font-semibold text-gray-900">{info.value}</p>
                    {info.subValue && (
                      <p className="text-gray-900">{info.subValue}</p>
                    )}
                    <p className="text-sm text-gray-600 mt-2">{info.description}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Map Section */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            <div className="relative aspect-video">
              <img
                src="https://i.ibb.co/bgMsZ97x/Screenshot-2025-01-29-at-8-51-18-PM.png"
                alt="UCX Office Location Map"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <div className="absolute bottom-4 left-4 right-4">
                <div className="bg-white/90 backdrop-blur-sm rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-1">UCX Headquarters</h3>
                  <p className="text-gray-600 text-sm">1348 Hwy 287, Suite 300, Waxahachie, TX 75165</p>
                  <a
                    href="https://www.google.ca/maps/place/Unified+ConneXions/@32.4232846,-96.8506022,17.34z/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 mt-2 text-sm group"
                  >
                    Get Directions
                    <ExternalLink className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl blur-xl opacity-10"></div>
            <div className="relative bg-white rounded-xl shadow-xl p-8 border border-gray-100">
              <div className="flex items-center mb-8">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg text-blue-600 mr-4">
                  <MessageSquare className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Send Us a Message</h2>
                  <p className="text-gray-600 mt-1">We'll get back to you promptly</p>
                </div>
              </div>

              {state.succeeded ? (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                    <CheckCircle className="h-8 w-8 text-green-500" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                  <p className="text-gray-600">
                    Thanks for reaching out. We'll get back to you soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="How can we help you?"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={state.submitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {state.submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                        Sending...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}